import { useMutation } from '@tanstack/react-query';
import { USER_ROLE } from 'khufu-legos';
import { CloseButton, Col, Form, Modal as ModalComponent } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { registerUser } from '../../apis/queries';
import SubmitButton from '../../components/Buttons/SubmitButton';
import { isValidPhoneNumber } from '../../helpers/validation';
import { UserDocWithPassword } from '../../types';
import DisplayFieldErrors from '../../utils/DisplayFormError';

interface CreateUserModalProps {
  show: boolean;
  handleClose: () => void;
  onUserCreated: (password: string) => void;
}

interface CreateUserFormInput {
  email: string;
  role: USER_ROLE;
  fullName: string;
  nickName: string;
  phoneNumber: string;
}

const CreateUserModal = ({ show, handleClose, onUserCreated }: CreateUserModalProps) => {
  const mutation = useMutation({
    mutationFn: (user: CreateUserFormInput) => {
      return registerUser(user);
    },
    onSuccess: data => {
      const newUser = data.data as UserDocWithPassword;
      if (newUser?.password) {
        onUserCreated(newUser?.password);
      }

      handleClose();
    }
  });

  const loading = mutation?.isPending;

  const methods = useForm<CreateUserFormInput>({
    mode: 'onTouched',
    defaultValues: {
      email: '',
      role: USER_ROLE.ADMIN,
      fullName: '',
      nickName: '',
      phoneNumber: ''
    }
  });

  const {
    getValues,
    register,
    formState: { isValid, errors }
  } = methods;

  const onCreateUser = () => {
    const fields = getValues();
    mutation?.mutate(fields);
  };

  return (
    <ModalComponent show={show} onHide={handleClose} centered>
      <ModalComponent.Header>
        <h4 className="card-header-title">Create User</h4>
        <CloseButton onClick={handleClose} />
      </ModalComponent.Header>
      <ModalComponent.Body className="overflow-auto d-flex flex-column flex-grow-1">
        <Form>
          <Form.Group className="my-2">
            <Form.Label htmlFor="email">Email Address</Form.Label>
            <Form.Control
              type="email"
              id="email"
              placeholder="name@address.com"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email address'
                }
              })}
              autoFocus
            />
            {errors.email && <DisplayFieldErrors displayText={errors.email.message!} />}
          </Form.Group>
          <Form.Group className="my-2">
            <Form.Label htmlFor="fullName">Full Name</Form.Label>
            <Form.Control
              type="text"
              id="fullName"
              placeholder="John Doe"
              {...register('fullName', {
                required: 'Full name is required',
                validate: value => value.trim() !== '' || 'Full name cannot be empty',
                pattern: {
                  value: /^[a-zA-Z]+( [a-zA-Z]+)*$/,
                  message: 'Full name should contain only alphabets'
                }
              })}
              onInput={e => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, '');
              }}
              autoFocus
            />
            {errors?.fullName && <DisplayFieldErrors displayText={errors.fullName.message!} />}
          </Form.Group>
          <Form.Group className="my-2">
            <Form.Label htmlFor="nickName">Nickname</Form.Label>
            <Form.Control
              type="text"
              id="nickName"
              placeholder="johnny"
              {...register('nickName', {
                required: 'Nickname is required',
                validate: value => value.trim() !== '' || 'Nickname cannot be empty',
                pattern: {
                  value: /^[a-zA-Z]+$/,
                  message: 'Nickname should contain only alphabets'
                }
              })}
              onInput={e => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, '');
              }}
              autoFocus
            />
            {errors.nickName && <DisplayFieldErrors displayText={errors.nickName.message!} />}
          </Form.Group>
          <Form.Group className="my-2">
            <Form.Label htmlFor="phoneNumber">Phone Number</Form.Label>
            <Form.Control
              type="tel"
              id="phoneNumber"
              placeholder="08012345678"
              {...register('phoneNumber', {
                required: 'Phone number is required',
                pattern: {
                  value: /^[0-9]+$/,
                  message: 'Phone number must contain only digits'
                },
                validate: {
                  isNotEmpty: value => value.trim() !== '' || 'Phone number cannot be empty',
                  isValidFormat: isValidPhoneNumber
                }
              })}
              autoFocus
              onInput={e => {
                e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');
              }}
            />
            {errors?.phoneNumber && <DisplayFieldErrors displayText={errors.phoneNumber.message!} />}
          </Form.Group>
          <Form.Group className="my-2">
            <Form.Label htmlFor="role">Role</Form.Label>
            <Form.Select id="role" {...register('role', { required: true })} autoFocus>
              <option value="admin">Admin</option>
              <option value="finance">Finance</option>
              <option value="supervisor">Supervisor</option>
              <option value="stockKeeper">Stock Keeper</option>
            </Form.Select>
          </Form.Group>
        </Form>
      </ModalComponent.Body>
      <ModalComponent.Footer>
        <Col className="d-grid">
          <SubmitButton buttonText="cancel" variant="white" onClick={handleClose} disabled={loading} loading={false} />
        </Col>
        <Col className="d-grid">
          <SubmitButton
            buttonText="Create User"
            onClick={onCreateUser}
            loading={loading}
            disabled={!isValid || loading}
          />
        </Col>
      </ModalComponent.Footer>
    </ModalComponent>
  );
};

export default CreateUserModal;
